import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { RplStatusClient, RplStatusDTO } from '../services/apiClient';
import { API_BASE_URL } from '../config/config';
import axiosInstance from '../services/axiosInstance';

const apiClient = new RplStatusClient(API_BASE_URL, axiosInstance);

// Cached lookup for dropdowns (infinite stale time)
export const useRplStatuses = () => {
  return useQuery({
    queryKey: ['rplStatuses'],
    queryFn: async () => {
      const response = await apiClient.searchRplStatuses({
        page: 1,
        pageSize: 9999,
        sortBy: 'DisplayPosition',
        sortDescending: false,
      });
      return response?.data?.results || [];
    },
    staleTime: 4 * 60 * 60 * 1000, // 4 hours
    refetchOnReconnect: true,
  });
};

// Non-cached search for List pages
export const useRplStatusSearch = () => {
  const search = async (filters: any) => {
    const response = await apiClient.searchRplStatuses(filters);
    return {
      results: response?.data?.results || [],
      totalCount: response?.data?.totalCount || 0,
    };
  };

  return { search };
};

// Get single item by ID
export const useRplStatusById = (id: string) => {
  return useQuery({
    queryKey: ['rplStatus', id],
    queryFn: async () => {
      const response = await apiClient.getRplStatusById(id);
      return response?.data;
    },
    enabled: !!id,
  });
};

// Create mutation
export const useCreateRplStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: RplStatusDTO) => {
      return await apiClient.createRplStatus(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rplStatuses'] });
    },
  });
};

// Update mutation
export const useUpdateRplStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: RplStatusDTO }) => {
      return await apiClient.updateRplStatus(id, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rplStatuses'] });
    },
  });
};

// Delete mutation
export const useDeleteRplStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      return await apiClient.deleteRplStatus(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rplStatuses'] });
    },
  });
};